// The dashboards (spec/21 §6), as data.
//
// Nine dashboards: the eight spec/21 §6 asks for, plus one for the cost of telemetry itself,
// because the rules in spec/19 §7 are build requirements and a build requirement with no gauge on
// it is a build requirement nobody can tell has stopped holding.
//
// Every panel names metrics from the catalogue. Where the catalogue says nothing emits a metric
// yet, the generated dashboard renders the panel with "no data source yet — <what has to land>"
// across it and no query at all. That is deliberate: a panel that queries a metric nothing writes
// draws a flat zero, and a flat zero is indistinguishable from a quiet night.
import { findMetric } from "./metrics.js";
export const PANEL_KINDS = ['timeseries', 'stat', 'table', 'heatmap', 'bargauge'];
const HTTP_ERROR = [{ attribute: 'http.response.status_code', op: '=~', value: '5..' }];
const CATALOGUE = [
    // -------------------------------------------------------------------------------------------
    {
        id: 'slo',
        title: 'Service level objectives',
        description: 'The spec/21 §1 table, with each target drawn as a threshold. The one dashboard that says ' +
            'whether the platform is keeping its promises this month.',
        specifiedAs: 'the SLOs above',
        panels: [
            {
                id: 'availability',
                title: 'API availability',
                description: '99.9% monthly. The error budget is about 43 minutes a month; this is how much is left.',
                kind: 'stat',
                slo: 'api-availability',
                queries: [
                    {
                        kind: 'ratio',
                        legend: 'successful requests',
                        metric: 'http.server.request.duration',
                        filters: [{ attribute: 'http.response.status_code', op: '!~', value: '5..' }],
                        window: '30d',
                    },
                ],
            },
            {
                id: 'read-latency',
                title: 'Read latency',
                description: 'p50 < 100 ms, p95 < 400 ms, p99 < 800 ms. GET only.',
                kind: 'timeseries',
                slo: 'api-read-latency',
                queries: [
                    {
                        kind: 'quantile',
                        quantile: 0.5,
                        legend: 'p50',
                        metric: 'http.server.request.duration',
                        filters: [{ attribute: 'http.request.method', op: '=', value: 'GET' }],
                    },
                    {
                        kind: 'quantile',
                        quantile: 0.95,
                        legend: 'p95',
                        metric: 'http.server.request.duration',
                        filters: [{ attribute: 'http.request.method', op: '=', value: 'GET' }],
                    },
                    {
                        kind: 'quantile',
                        quantile: 0.99,
                        legend: 'p99',
                        metric: 'http.server.request.duration',
                        filters: [{ attribute: 'http.request.method', op: '=', value: 'GET' }],
                    },
                ],
            },
            {
                id: 'write-latency',
                title: 'Write latency',
                description: 'p95 < 600 ms. Everything that is not a GET.',
                kind: 'timeseries',
                slo: 'api-write-latency',
                queries: [
                    {
                        kind: 'quantile',
                        quantile: 0.95,
                        legend: 'p95',
                        metric: 'http.server.request.duration',
                        filters: [{ attribute: 'http.request.method', op: '!=', value: 'GET' }],
                    },
                ],
            },
            {
                id: 'kiosk-sign-in',
                title: 'Sign-in to playable desktop',
                description: '≤ 3 s p95 on venue broadband, measured on the kiosk.',
                kind: 'timeseries',
                slo: 'kiosk-sign-in',
                queries: [{ kind: 'quantile', quantile: 0.95, legend: 'p95', metric: 'clavian.session.sign_in.duration' }],
            },
            {
                id: 'command-ack',
                title: 'Staff action → PC reacts',
                description: '≤ 1 s p95 for the station to acknowledge a command.',
                kind: 'timeseries',
                slo: 'command-ack',
                queries: [
                    { kind: 'quantile', quantile: 0.95, legend: 'p95', metric: 'clavian.realtime.command.ack.duration' },
                ],
            },
            {
                id: 'realtime-delivery',
                title: 'Realtime delivery',
                description: '≤ 500 ms p95 from the event to the client holding it.',
                kind: 'timeseries',
                slo: 'realtime-delivery',
                queries: [{ kind: 'quantile', quantile: 0.95, legend: 'p95', metric: 'clavian.realtime.delivery.duration' }],
            },
            {
                id: 'payment-webhook',
                title: 'Payment webhook → order paid',
                description: '≤ 5 s p95 after the provider sends it.',
                kind: 'timeseries',
                slo: 'payment-webhook',
                queries: [{ kind: 'quantile', quantile: 0.95, legend: 'p95', metric: 'clavian.payment.webhook.latency' }],
            },
            {
                id: 'play-time-integrity',
                title: 'Play time lost or invented',
                description: 'Zero, provable from the ledger and the segments. Anything else is a page.',
                kind: 'stat',
                slo: 'play-time-integrity',
                queries: [{ kind: 'value', legend: 'imbalance', metric: 'clavian.ledger.imbalance', by: ['clavian.currency'] }],
            },
        ],
    },
    // -------------------------------------------------------------------------------------------
    {
        id: 'api',
        title: 'API — per endpoint',
        description: 'Which endpoint is slow and which endpoint is failing, without reading a log.',
        specifiedAs: 'per-endpoint latency and error rate',
        panels: [
            {
                id: 'latency-by-route',
                title: 'p95 latency by route',
                description: 'The route template, never the path: /v1/venues/{venueId} is one series, not ten thousand.',
                kind: 'timeseries',
                queries: [
                    {
                        kind: 'quantile',
                        quantile: 0.95,
                        legend: '{{http_route}}',
                        metric: 'http.server.request.duration',
                        by: ['http.route'],
                    },
                ],
            },
            {
                id: 'error-rate-by-route',
                title: '5xx rate by route',
                description: 'A route that has started failing. 4xx is excluded: a rejected request is the API working.',
                kind: 'timeseries',
                queries: [
                    {
                        kind: 'rate',
                        legend: '{{http_route}}',
                        metric: 'http.server.request.duration',
                        filters: HTTP_ERROR,
                        by: ['http.route'],
                    },
                ],
            },
            {
                id: 'throughput',
                title: 'Requests per second by entrypoint',
                description: 'api, realtime and worker from one image; this is how the load divides between them.',
                kind: 'timeseries',
                queries: [
                    {
                        kind: 'rate',
                        legend: '{{clavian_entrypoint}}',
                        metric: 'http.server.request.duration',
                        by: ['clavian.entrypoint'],
                    },
                ],
            },
            {
                id: 'in-flight',
                title: 'Requests in flight',
                description: 'Saturation. The number the horizontal autoscaler reads alongside CPU (spec/21 §9).',
                kind: 'timeseries',
                queries: [
                    {
                        kind: 'value',
                        legend: '{{clavian_entrypoint}}',
                        metric: 'http.server.active_requests',
                        by: ['clavian.entrypoint'],
                    },
                ],
            },
            {
                id: 'db-latency',
                title: 'Database statement latency',
                description: 'p95 per operation. A route that got slower usually got slower here first.',
                kind: 'timeseries',
                queries: [
                    {
                        kind: 'quantile',
                        quantile: 0.95,
                        legend: '{{db_operation_name}}',
                        metric: 'db.client.operation.duration',
                        by: ['db.operation.name'],
                    },
                ],
            },
            {
                id: 'valkey-latency',
                title: 'Valkey command latency',
                description: 'p95 per command. Presence, the caches and the rate-limit counters all run through here.',
                kind: 'timeseries',
                queries: [
                    {
                        kind: 'quantile',
                        quantile: 0.95,
                        legend: '{{clavian_valkey_command}}',
                        metric: 'clavian.valkey.command.duration',
                        by: ['clavian.valkey.command'],
                    },
                ],
            },
            {
                id: 'errors-by-release',
                title: 'Errors by release',
                description: 'Tagged with service.version, so a bad deploy is a step change rather than a hunch.',
                kind: 'timeseries',
                queries: [{ kind: 'rate', legend: '{{service_version}}', metric: 'clavian.errors', by: ['service.version'] }],
            },
        ],
    },
    // -------------------------------------------------------------------------------------------
    {
        id: 'realtime',
        title: 'Realtime gateway',
        description: 'Sockets held, frames delivered, and how long delivery took.',
        specifiedAs: 'realtime connection counts and delivery latency',
        panels: [
            {
                id: 'connections',
                title: 'Open sockets per node',
                description: 'Sized against the socket ceiling in spec/19 §2: three nodes, any two of which carry the fleet.',
                kind: 'timeseries',
                queries: [
                    { kind: 'value', legend: '{{clavian_node}}', metric: 'clavian.realtime.connections', by: ['clavian.node'] },
                ],
            },
            {
                id: 'connections-by-kind',
                title: 'Sockets by principal',
                description: 'Stations, members, players. A reconnect storm shows here before it shows as latency.',
                kind: 'timeseries',
                queries: [
                    {
                        kind: 'value',
                        legend: '{{clavian_principal_kind}}',
                        metric: 'clavian.realtime.connections',
                        by: ['clavian.principal.kind'],
                    },
                ],
            },
            {
                id: 'delivery-latency',
                title: 'Delivery latency',
                description: 'Event to client, p50 and p95. The SLO is 500 ms at p95.',
                kind: 'timeseries',
                slo: 'realtime-delivery',
                queries: [
                    { kind: 'quantile', quantile: 0.5, legend: 'p50', metric: 'clavian.realtime.delivery.duration' },
                    { kind: 'quantile', quantile: 0.95, legend: 'p95', metric: 'clavian.realtime.delivery.duration' },
                ],
            },
            {
                id: 'reconnects',
                title: 'Reconnects',
                description: 'Sockets authenticating per second, by principal. A step with no deploy behind it is a reconnect ' +
                    'storm (spec/19 §7 rule 9); a deploy should show a 30-second ramp, not a spike.',
                kind: 'timeseries',
                queries: [
                    {
                        kind: 'rate',
                        legend: '{{clavian_principal_kind}}',
                        metric: 'clavian.realtime.connects',
                        by: ['clavian.principal.kind'],
                    },
                ],
            },
            {
                id: 'slow-consumers',
                title: 'Slow consumers',
                description: 'Sockets switched to merged updates past 1 MiB unsent, and closed past 4 MiB (spec/09 §8).',
                kind: 'timeseries',
                queries: [
                    {
                        kind: 'rate',
                        legend: '{{clavian_backpressure_action}}',
                        metric: 'clavian.realtime.slow_consumers',
                        by: ['clavian.backpressure.action'],
                    },
                ],
            },
            {
                id: 'closes',
                title: 'Closes by code',
                description: 'Why the gateway closed sockets (spec/09 §9). A wall of 4401 is a token or clock problem.',
                kind: 'timeseries',
                queries: [{ kind: 'rate', legend: '{{clavian_close_code}}', metric: 'clavian.realtime.closes', by: ['clavian.close.code'] }],
            },
            {
                id: 'command-ack',
                title: 'Command acknowledgement',
                description: 'Staff pressed a button; the station said it had done it. ≤ 1 s at p95.',
                kind: 'timeseries',
                slo: 'command-ack',
                queries: [
                    {
                        kind: 'quantile',
                        quantile: 0.95,
                        legend: '{{clavian_command}}',
                        metric: 'clavian.realtime.command.ack.duration',
                        by: ['clavian.command'],
                    },
                ],
            },
        ],
    },
    // -------------------------------------------------------------------------------------------
    {
        id: 'queues',
        title: 'Queues and jobs',
        description: 'Queue depth and job lag. The outbox is the one queue whose backlog is a business problem: ' +
            'an undispatched event is an effect that has not happened yet (spec/03 §8).',
        specifiedAs: 'queue depth and job lag',
        panels: [
            {
                id: 'outbox-backlog',
                title: 'Outbox backlog',
                description: 'Events written and not yet dispatched. Steady state is near zero.',
                kind: 'timeseries',
                queries: [{ kind: 'value', legend: 'undispatched', metric: 'clavian.outbox.backlog' }],
                threshold: { value: 1000, label: 'ticket above this for 15 minutes' },
            },
            {
                id: 'outbox-lag',
                title: 'Outbox lag',
                description: 'How old the oldest undispatched row is. This, not the count, is what says the dispatcher ' +
                    'has stopped: ten rows four hours old is worse than ten thousand two seconds old.',
                kind: 'timeseries',
                queries: [{ kind: 'value', legend: 'oldest undispatched', metric: 'clavian.outbox.lag' }],
                threshold: { value: 300_000, label: 'five minutes' },
            },
            {
                id: 'outbox-dead-letter',
                title: 'Dead-lettered deliveries',
                description: 'Deliveries that ran out of attempts. Above zero, an event never had its effect.',
                kind: 'stat',
                queries: [{ kind: 'value', legend: 'dead lettered', metric: 'clavian.outbox.dead_lettered' }],
                threshold: { value: 0, label: 'anything above zero is a ticket' },
            },
            {
                id: 'outbox-retrying',
                title: 'Deliveries waiting on a backoff',
                description: 'Healthy in ones. Hundreds is a provider outage, and the retry job is riding it out.',
                kind: 'timeseries',
                queries: [{ kind: 'value', legend: 'retrying', metric: 'clavian.outbox.retrying' }],
            },
            {
                id: 'delivery-outcomes',
                title: 'Deliveries by sink',
                description: 'Handlers, realtime fan-out, webhooks, notifications — which sink is failing.',
                kind: 'timeseries',
                queries: [
                    {
                        kind: 'rate',
                        legend: '{{clavian_delivery_sink}} {{clavian_delivery_outcome}}',
                        metric: 'clavian.outbox.deliveries',
                        by: ['clavian.delivery.sink', 'clavian.delivery.outcome'],
                    },
                ],
            },
            {
                id: 'queue-depth',
                title: 'pg-boss queue depth',
                description: 'Ready, active and failed per queue. Ready climbing with active flat is a worker that has died.',
                kind: 'timeseries',
                queries: [
                    {
                        kind: 'value',
                        legend: '{{clavian_queue}} {{clavian_queue_state}}',
                        metric: 'clavian.queue.depth',
                        by: ['clavian.queue', 'clavian.queue.state'],
                    },
                ],
            },
            {
                id: 'job-duration',
                title: 'Scheduled job duration',
                description: 'p95 per job. A job that starts taking minutes is the next backlog.',
                kind: 'timeseries',
                queries: [
                    {
                        kind: 'quantile',
                        quantile: 0.95,
                        legend: '{{clavian_job}}',
                        metric: 'clavian.job.duration',
                        by: ['clavian.job'],
                    },
                ],
            },
            {
                id: 'job-runs',
                title: 'Job runs by outcome',
                description: 'A job that has stopped running has a flat line here, which is the failure a duration ' +
                    'panel cannot show.',
                kind: 'timeseries',
                queries: [
                    {
                        kind: 'rate',
                        legend: '{{clavian_job}} {{clavian_job_outcome}}',
                        metric: 'clavian.job.runs',
                        by: ['clavian.job', 'clavian.job.outcome'],
                    },
                ],
            },
        ],
    },
    // -------------------------------------------------------------------------------------------
    {
        id: 'fleet',
        title: 'Station fleet health',
        description: 'How many stations are up, where, and what they are saying about themselves.',
        specifiedAs: 'station fleet health',
        panels: [
            {
                id: 'online',
                title: 'Stations online by venue',
                description: 'A venue whose line drops to zero during opening hours is a page.',
                kind: 'timeseries',
                queries: [
                    { kind: 'value', legend: '{{clavian_venue}}', metric: 'clavian.station.online', by: ['clavian.venue'] },
                ],
            },
            {
                id: 'health-checks',
                title: 'Station self-reported health',
                description: 'Disk, memory, the keyboard hook, the policy version (spec/10 §9).',
                kind: 'table',
                queries: [
                    {
                        kind: 'value',
                        legend: '{{clavian_venue}} {{clavian_station_health_check}}',
                        metric: 'clavian.station.health',
                        by: ['clavian.venue', 'clavian.station.health.check'],
                    },
                ],
            },
            {
                id: 'heartbeats',
                title: 'Heartbeat rate',
                description: 'Counted, never logged. At 50,000 stations on a 30-second refresh this is about 1,600 a ' +
                    'second, and one log line each would be the largest line on the bill (spec/19 §7 rule 1).',
                kind: 'timeseries',
                queries: [
                    {
                        kind: 'rate',
                        legend: '{{clavian_heartbeat_outcome}}',
                        metric: 'clavian.station.heartbeats',
                        by: ['clavian.heartbeat.outcome'],
                    },
                ],
            },
            {
                id: 'presence-changes',
                title: 'Presence changes',
                description: 'The only part of a heartbeat that reaches Postgres. A spike here with no deploy behind ' +
                    'it is a reconnect storm (spec/19 §7 rule 9).',
                kind: 'timeseries',
                queries: [
                    {
                        kind: 'rate',
                        legend: '{{clavian_station_change}}',
                        metric: 'clavian.station.presence_changes',
                        by: ['clavian.station.change'],
                    },
                ],
            },
        ],
    },
    // -------------------------------------------------------------------------------------------
    {
        id: 'payments',
        title: 'Payments and settlement',
        description: 'Whether money is arriving, and how long it takes to arrive.',
        specifiedAs: 'payment success and settlement lag',
        panels: [
            {
                id: 'success-rate',
                title: 'Payment success rate',
                description: 'By provider and method. A drop with no traffic change is a provider incident.',
                kind: 'timeseries',
                queries: [
                    {
                        kind: 'ratio',
                        legend: '{{clavian_payment_provider}} {{clavian_payment_method}}',
                        metric: 'clavian.payment.attempts',
                        filters: [{ attribute: 'clavian.payment.outcome', op: '=', value: 'succeeded' }],
                        by: ['clavian.payment.provider', 'clavian.payment.method'],
                    },
                ],
            },
            {
                id: 'webhook-latency',
                title: 'Webhook → order paid',
                description: '≤ 5 s p95 after the provider sends it. Beyond that, a player is staring at a spinner.',
                kind: 'timeseries',
                slo: 'payment-webhook',
                queries: [{ kind: 'quantile', quantile: 0.95, legend: 'p95', metric: 'clavian.payment.webhook.latency' }],
            },
            {
                id: 'settlement-lag',
                title: 'Settlement lag',
                description: 'Capture to settled funds, per provider. A venue notices this before anybody else does.',
                kind: 'timeseries',
                queries: [
                    {
                        kind: 'quantile',
                        quantile: 0.95,
                        legend: '{{clavian_payment_provider}}',
                        metric: 'clavian.payment.settlement.lag',
                        by: ['clavian.payment.provider'],
                    },
                ],
            },
            {
                id: 'refunds',
                title: 'Refunds',
                description: 'A spike is either a bad release or a fraud pattern, and both want somebody now.',
                kind: 'timeseries',
                queries: [
                    { kind: 'rate', legend: '{{clavian_venue}}', metric: 'clavian.refunds', by: ['clavian.venue'] },
                ],
            },
        ],
    },
    // -------------------------------------------------------------------------------------------
    {
        id: 'ledger',
        title: 'Ledger integrity',
        description: 'The one dashboard whose correct reading is a flat zero. Play time lost or invented is a ' +
            'promise in spec/21 §1, and this is where it is kept.',
        specifiedAs: 'ledger integrity job results',
        panels: [
            {
                id: 'imbalance',
                title: 'Debits minus credits',
                description: 'At the last integrity run, per currency. Anything but zero pages somebody.',
                kind: 'stat',
                slo: 'play-time-integrity',
                queries: [
                    {
                        kind: 'value',
                        legend: '{{clavian_currency}}',
                        metric: 'clavian.ledger.imbalance',
                        by: ['clavian.currency'],
                    },
                ],
                threshold: { value: 0, label: 'the only acceptable value' },
            },
            {
                id: 'runs',
                title: 'Integrity runs',
                description: 'By outcome. A job that stopped running is as bad as one that failed and is much easier ' +
                    'to miss, so the absence of a run is what this panel is for.',
                kind: 'timeseries',
                queries: [
                    {
                        kind: 'rate',
                        legend: '{{clavian_job_outcome}}',
                        metric: 'clavian.ledger.integrity.runs',
                        by: ['clavian.job.outcome'],
                    },
                ],
            },
        ],
    },
    // -------------------------------------------------------------------------------------------
    {
        id: 'messaging-cost',
        title: 'OTP and notification volume and cost',
        description: 'OTP is the biggest variable cost in the product: WhatsApp is about a sixtieth of the price ' +
            'of SMS, so the split between the two is the number that matters (spec/19 §2, §7 rules 3–4).',
        specifiedAs: 'OTP/notification volume and cost',
        panels: [
            {
                id: 'otp-volume',
                title: 'OTP volume by channel',
                description: 'WhatsApp first, DLT-registered SMS as the fallback: SMS climbing means WhatsApp is failing. Email carries an ' +
                    'address’s verification, sign-in and reset codes. Every attempt, by outcome — `unavailable` is a code no provider could carry.',
                kind: 'timeseries',
                queries: [
                    {
                        kind: 'rate',
                        legend: '{{clavian_message_channel}}',
                        metric: 'clavian.otp.sent',
                        by: ['clavian.message.channel'],
                    },
                ],
            },
            {
                id: 'otp-cost',
                title: 'OTP spend',
                description: 'Rolling 24 hours, in the billing currency minor unit.',
                kind: 'stat',
                queries: [
                    {
                        kind: 'total',
                        legend: '{{clavian_message_channel}}',
                        metric: 'clavian.otp.cost',
                        by: ['clavian.message.channel', 'clavian.currency'],
                        window: '24h',
                    },
                ],
            },
            {
                id: 'notification-volume',
                title: 'Notification volume',
                description: 'By channel and category. Push and inbox are free; WhatsApp, SMS and email are not.',
                kind: 'timeseries',
                queries: [
                    {
                        kind: 'rate',
                        legend: '{{clavian_message_channel}} {{clavian_notification_category}}',
                        metric: 'clavian.notification.sent',
                        by: ['clavian.message.channel', 'clavian.notification.category'],
                    },
                ],
            },
            {
                id: 'notification-cost',
                title: 'Notification spend',
                description: 'Rolling 24 hours, in the billing currency minor unit.',
                kind: 'stat',
                queries: [
                    {
                        kind: 'total',
                        legend: '{{clavian_message_channel}}',
                        metric: 'clavian.notification.cost',
                        by: ['clavian.message.channel', 'clavian.currency'],
                        window: '24h',
                    },
                ],
            },
        ],
    },
    // -------------------------------------------------------------------------------------------
    {
        id: 'telemetry-cost',
        title: 'The cost of telemetry',
        description: 'The rules in spec/19 §7 are build requirements, so they have gauges. Suppressed lines ' +
            'climbing while written lines stay flat is the heartbeat rule doing its job; both climbing ' +
            'together is somebody having added a log line to a keepalive path.',
        specifiedAs: 'cost discipline in telemetry (spec/21 §6, spec/19 §7 rule 5)',
        panels: [
            {
                id: 'log-lines',
                title: 'Log lines written',
                description: 'By level. This is what the log store is billed for.',
                kind: 'timeseries',
                queries: [
                    {
                        kind: 'rate',
                        legend: '{{clavian_log_level}}',
                        metric: 'clavian.telemetry.log_lines',
                        by: ['clavian.log.level'],
                    },
                ],
            },
            {
                id: 'log-lines-suppressed',
                title: 'Log lines the cost rule refused',
                description: 'By operation. Heartbeats and health checks, counted rather than written. A rule whose ' +
                    'only evidence is an absence is a rule nobody can tell has stopped working.',
                kind: 'timeseries',
                queries: [
                    {
                        kind: 'rate',
                        legend: '{{clavian_operation}}',
                        metric: 'clavian.telemetry.log_lines_suppressed',
                        by: ['clavian.operation'],
                    },
                ],
            },
            {
                id: 'sampling',
                title: 'Sampling decisions',
                description: 'Recorded, dropped by the ratio, dropped because the operation is silent. If "dropped" ' +
                    'is flat at zero in production, the sampler is configured and not sampling.',
                kind: 'timeseries',
                queries: [
                    {
                        kind: 'rate',
                        legend: '{{clavian_sampling_decision}}',
                        metric: 'clavian.telemetry.sampling_decisions',
                        by: ['clavian.sampling.decision'],
                    },
                ],
            },
            {
                id: 'readiness',
                title: 'Readiness failures',
                description: 'What a failing /readyz reports instead of a log line. The probe itself is never logged ' +
                    'and never traced.',
                kind: 'timeseries',
                queries: [
                    {
                        kind: 'rate',
                        legend: '{{clavian_check}}',
                        metric: 'clavian.readiness.failures',
                        by: ['clavian.check'],
                    },
                ],
            },
        ],
    },
];
export const DASHBOARDS = CATALOGUE;
const BY_ID = new Map(DASHBOARDS.map((board) => [board.id, board]));
export function findDashboard(id) {
    return BY_ID.get(id);
}
/** Every metric a panel reads. Unknown names are dropped here and caught by the catalogue test. */
export function panelMetrics(panel) {
    const seen = new Map();
    for (const query of panel.queries) {
        const metric = findMetric(query.metric);
        if (metric !== undefined)
            seen.set(metric.name, metric);
    }
    return [...seen.values()];
}
/**
 * True when nothing in the repository can write any of the metrics this panel reads.
 *
 * The generator renders these with "no data source yet" and no query, rather than with a query
 * that would draw a confident flat zero (spec/21 §6; the pull request for issue #40 lists them).
 */
export function panelHasNoDataSource(panel) {
    const metrics = panelMetrics(panel);
    return metrics.length > 0 && metrics.every((metric) => metric.source.kind === 'pending');
}
/** A dashboard nothing can fill yet, for the list the pull request has to be explicit about. */
export function dashboardHasNoDataSource(dashboard) {
    return dashboard.panels.length > 0 && dashboard.panels.every(panelHasNoDataSource);
}
//# sourceMappingURL=dashboards.js.map